import { useEffect, useReducer } from 'react'
import { ChatChannelInstance, ChatLocalMesage, MessageStatusTypes } from '../types'
import { messageReceiptStage, receiptHasOtherUser, type ChatMessageStage } from '../utils/chat'

/**
 * In-conversation message tick stage (clock / single check / grey double /
 * highlighted double), derived from Stream's real delivery + read receipts
 * (`channel.messageReceiptsTracker`) instead of the Stream-server ACK
 * (`message.status === 'received'`) or the per-platform `readBy` prop.
 *
 * Re-renders on `message.delivered` / `message.read` / `message.new` so the
 * tick advances live, mirroring Stream's own `useMessageDeliveryStatus`. Both
 * `delivered` and `read` are recomputed from the tracker on each of those
 * events, so they stay reactive; the caller's `readByOther` is OR-ed in only as
 * a fallback for platforms whose existing `readBy` path already works.
 *
 * The pure pieces (`receiptHasOtherUser` / `messageReceiptStage`) live in
 * `utils/chat.ts` with the other pure chat helpers; this hook is just the
 * reactive glue that re-renders them on Stream events.
 *
 * Context: Connect Issues —
 *  - the chat list / in-conversation tick showed the delivered grey double-check
 *    before the recipient's device had received the message (the `sent` split);
 *  - the blue read-check never advanced for own messages until an unrelated
 *    re-render (next send / reply / re-open). The tracker held the read state
 *    correctly all along — the `readBy` prop path simply wasn't reactive, and
 *    its `readBy > 1` count never counted the sender for photo/poll messages.
 *
 * @example
 * const stage = useMessageReceiptStage({ channel, message, currentUserId, isMine, readByOther })
 * if (stage === 'sent') return <SingleCheck />
 */
export function useMessageReceiptStage({
  channel,
  message,
  currentUserId,
  isMine,
  readByOther,
}: {
  channel?: ChatChannelInstance
  message?: ChatLocalMesage
  currentUserId?: string
  isMine: boolean
  readByOther: boolean
}): ChatMessageStage | null {
  const [, bump] = useReducer((n: number) => n + 1, 0)

  useEffect(() => {
    if (!channel || !isMine) return
    const subs = [
      channel.on('message.delivered', bump),
      channel.on('message.read', bump),
      channel.on('message.new', bump),
    ]
    return () => subs.forEach(sub => sub?.unsubscribe?.())
  }, [channel, isMine])

  if (!isMine) return null

  // Read is taken from the tracker so the blue check advances live on
  // `message.read`; the caller's `readByOther` is OR-ed in only as a fallback.
  const readByOtherResolved =
    receiptHasOtherUser('readersForMessage', channel, message, currentUserId) || readByOther

  return messageReceiptStage({
    isMine,
    isFailed: message?.status === MessageStatusTypes.FAILED || message?.type === 'error',
    isSending: message?.status === MessageStatusTypes.SENDING,
    readByOther: readByOtherResolved,
    deliveredToOther: receiptHasOtherUser('deliveredForMessage', channel, message, currentUserId),
  })
}
